import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Flame, Brain, Zap, Shield, TrendingUp } from 'lucide-react';

const values = [
  {
    icon: Target,
    title: 'Kaizen',
    subtitle: '1% Better Every Day',
    description: 'We believe in continuous improvement. Not overnight transformations — but the relentless, compounding work of getting marginally better at everything you do.',
    color: '#E63946',
  },
  {
    icon: Brain,
    title: 'Science-First',
    subtitle: 'Evidence Over Hype',
    description: 'Every protocol, supplement, and training methodology we recommend is grounded in peer-reviewed research. We cut through the noise so you don\'t have to.',
    color: '#00F0FF',
  },
  {
    icon: Shield,
    title: 'Discipline',
    subtitle: 'The Way of the Ninja',
    description: 'Discipline isn\'t about punishment — it\'s about creating systems that make excellence inevitable. We build tools and content that keep you accountable.',
    color: '#FFD100',
  },
  {
    icon: Zap,
    title: 'Optimization',
    subtitle: 'Hack the Defaults',
    description: 'Your body and mind have incredible untapped potential. Through biohacking, peptide science, and performance nutrition, we help you unlock what\'s already there.',
    color: '#22c55e',
  },
];

const stats = [
  { value: '98+', label: 'Research Articles' },
  { value: '8', label: 'Free Tools' },
  { value: '10', label: 'Content Categories' },
  { value: '1%', label: 'Better Every Day' },
];

export default function AboutPage() {
  return (
    <main className="pt-20 min-h-screen" data-testid="about-page">
      {/* Hero */}
      <section className="container-ninja section-spacing">
        <div className="max-w-4xl mx-auto">
          <div className="mb-16">
            <p className="text-ninja-label text-[#00F0FF] mb-4 animate-entrance">About NinjAthlete</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white font-heading mb-6 animate-entrance leading-tight" style={{ animationDelay: '0.1s' }} data-testid="about-title">
              We Build Athletes<br />
              <span className="text-gradient-ninja">Who Build Themselves</span>
            </h1>
            <p className="text-zinc-400 text-lg leading-relaxed max-w-2xl animate-entrance" style={{ animationDelay: '0.2s' }}>
              NinjAthlete was born from a simple obsession: what happens when you combine ancient warrior discipline
              with cutting-edge performance science? The answer is a new breed of athlete — one who optimizes
              every system in their body, tracks every metric that matters, and never stops improving.
            </p>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20" data-testid="about-stats">
            {stats.map((s, i) => (
              <div key={s.label} className="bg-zinc-900/50 border border-zinc-800 p-6 text-center animate-entrance" style={{ animationDelay: `${0.1 * (i + 1)}s` }}>
                <p className="text-3xl font-heading text-white">{s.value}</p>
                <p className="text-zinc-500 text-xs uppercase tracking-wider mt-1">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Story */}
          <div className="mb-20">
            <div className="flex items-center gap-3 mb-8">
              <span className="w-8 h-[2px] bg-[#E63946] inline-block" />
              <h2 className="text-2xl text-white font-heading">The Origin</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <p className="text-zinc-400 leading-relaxed">
                  The ninja weren't just warriors — they were the original biohackers. They studied herbalism,
                  breathwork, meditation, and physical conditioning centuries before any of it had a name.
                  They weren't stronger than samurai. They were <span className="text-white font-semibold">smarter</span>.
                </p>
                <p className="text-zinc-400 leading-relaxed">
                  NinjAthlete carries that philosophy into the modern era. We believe the best athletes aren't
                  just physically gifted — they're <span className="text-white font-semibold">strategically optimized</span>.
                  They understand their sleep architecture. They know their macros by heart. They track their
                  heart rate zones and periodize their training.
                </p>
              </div>
              <div className="space-y-4">
                <p className="text-zinc-400 leading-relaxed">
                  We started as a simple blog exploring the intersection of martial arts philosophy and
                  performance science. That grew into comprehensive guides on peptides, HYROX training,
                  nutrition protocols, and biohacking tools — all rooted in the Kaizen principle of
                  continuous improvement.
                </p>
                <p className="text-zinc-400 leading-relaxed">
                  Today, NinjAthlete is a platform built for people who refuse to accept "good enough."
                  Whether you're training for your first HYROX race, exploring peptide therapy, or just
                  trying to eat better — we give you the <span className="text-white font-semibold">science,
                  the tools, and the mindset</span> to get there.
                </p>
              </div>
            </div>
          </div>

          {/* Values */}
          <div className="mb-20">
            <div className="flex items-center gap-3 mb-8">
              <span className="w-8 h-[2px] bg-[#E63946] inline-block" />
              <h2 className="text-2xl text-white font-heading">What We Stand For</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="about-values">
              {values.map((v, i) => (
                <div key={v.title} className="bg-zinc-900/50 border border-zinc-800 p-6 hover:border-zinc-700 transition-colors group">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: `${v.color}15`, border: `1px solid ${v.color}30` }}>
                      <v.icon className="w-5 h-5" style={{ color: v.color }} />
                    </div>
                    <div>
                      <h3 className="text-white font-heading text-lg">{v.title}</h3>
                      <p className="text-zinc-600 text-[10px] uppercase tracking-wider">{v.subtitle}</p>
                    </div>
                  </div>
                  <p className="text-zinc-400 text-sm leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* What We Cover */}
          <div className="mb-20">
            <div className="flex items-center gap-3 mb-8">
              <span className="w-8 h-[2px] bg-[#E63946] inline-block" />
              <h2 className="text-2xl text-white font-heading">What We Cover</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { label: 'HYROX Training', desc: 'Race strategies, 8-week plans, gym directories', link: '/blog?category=HYROX' },
                { label: 'Peptide Science', desc: 'Protocols, dosing, reconstitution guides', link: '/blog?category=Peptides' },
                { label: 'Biohacking', desc: 'Cold exposure, fasting, nootropics', link: '/blog?category=Biohacking' },
                { label: 'Nutrition', desc: 'Meal prep, macros, supplements', link: '/blog?category=Nutrition' },
                { label: 'Fitness', desc: 'Strength training, bodyweight, mobility', link: '/blog?category=Fitness' },
                { label: 'Mindset', desc: 'Discipline, stoicism, growth quotes', link: '/blog?category=Motivation' },
              ].map((c, i) => (
                <Link key={c.label} to={c.link} className="bg-zinc-900/30 border border-zinc-800 p-4 hover:border-[#E63946]/30 transition-colors group" data-testid={`about-category-${i}`}>
                  <h4 className="text-white text-sm font-heading mb-1 group-hover:text-[#00F0FF] transition-colors">{c.label}</h4>
                  <p className="text-zinc-600 text-xs">{c.desc}</p>
                </Link>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-zinc-900/50 border border-zinc-800 p-8 md:p-12 text-center" data-testid="about-cta">
            <TrendingUp className="w-8 h-8 text-[#E63946] mx-auto mb-4" />
            <h2 className="text-3xl text-white font-heading mb-3">Start Your 1% Journey</h2>
            <p className="text-zinc-500 text-sm max-w-md mx-auto mb-6">
              Explore our free tools, dive into the science, and join a community that believes in the compound effect of daily improvement.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/tools" className="px-6 py-3 bg-[#E63946] text-white font-heading uppercase tracking-wider text-sm hover:bg-[#d62f3c] transition-colors inline-flex items-center gap-2" data-testid="about-tools-btn">
                Explore Tools <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/blog" className="px-6 py-3 border border-zinc-700 text-zinc-300 font-heading uppercase tracking-wider text-sm hover:text-white hover:border-zinc-500 transition-colors inline-flex items-center gap-2" data-testid="about-blog-btn">
                Read the Blog <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
